// Bill History Management for Local Storage and Firestore Cloud Sync
// This handles both anonymous and authenticated user history

import { doc, getDoc, setDoc, deleteDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';

const HISTORY_KEY = 'split_bill_history';
const MAX_HISTORY_ITEMS = 50; // Keep last 50 bills

export const BillHistory = {
  // Get all bills from history
  getHistory() {
    try {
      const history = localStorage.getItem(HISTORY_KEY);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error("Could not get bill history:", error);
      return [];
    }
  },

  // Add a bill to history (called when bill is completed)
  addToHistory(billData, calculationResults) {
    try {
      const historyItem = {
        id: billData.id,
        venueName: billData.venueName || 'Unknown Venue',
        timestamp: Date.now(),
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        total: calculationResults.grandTotal,
        participants: billData.people.map(p => p.name),
        participantCount: billData.people.length,
        itemCount: billData.items.length,
        status: 'completed',
        archived: false,
        // Mark if this is a combined bill
        isCombined: billData.isCombined || false,
        originalBills: billData.originalBills || [],
        // Store minimal bill data for restoration
        billSnapshot: {
          items: billData.items,
          tax: billData.tax,
          tip: billData.tip,
          receiptImage: billData.receiptImage
        },
        // Dining experience data (preserve from billData)
        experience: billData.experience || {
          venueRating: null, // 1-5 stars
          venueNotes: '', // General venue experience
          overallRating: null, // 1-5 stars for overall experience
          wouldReturn: null, // true/false
          dishRatings: {}, // itemId -> { rating: 1-5, notes: string, recommended: boolean }
          tags: [] // ["date night", "family friendly", "loud", "romantic", etc.]
        },
        // Store results for quick display
        summary: {
          transactions: calculationResults.transactions,
          breakdown: calculationResults.breakdown.map(p => ({
            name: p.name,
            finalShare: p.finalShare,
            paid: p.paid,
            balance: p.balance,
            items: (p.items || []).map(i => ({ name: i.name, price: i.price })),
            itemTotal: p.itemTotal || 0,
            taxAndTipShare: p.taxAndTipShare || 0
          }))
        }
      };

      let history = this.getHistory();
      
      // Remove if already exists (update case)
      history = history.filter(item => item.id !== billData.id);
      
      // Add to beginning
      history.unshift(historyItem);
      
      // Keep only recent items
      history = history.slice(0, MAX_HISTORY_ITEMS);
      
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
      return history;
    } catch (error) {
      console.error("Could not add to bill history:", error);
      return this.getHistory();
    }
  },

  // Toggle archive status
  toggleArchive(billId) {
    try {
      let history = this.getHistory();
      const billIndex = history.findIndex(item => item.id === billId);
      
      if (billIndex !== -1) {
        history[billIndex].archived = !history[billIndex].archived;
        localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
      }
      
      return history;
    } catch (error) {
      console.error("Could not toggle archive:", error);
      return this.getHistory();
    }
  },

  // Delete a bill from history
  deleteBill(billId) {
    try {
      let history = this.getHistory();
      history = history.filter(item => item.id !== billId);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
      return history;
    } catch (error) {
      console.error("Could not delete bill:", error);
      return this.getHistory();
    }
  },

  // Search and filter history
  searchHistory(query = '', filters = {}) {
    const history = this.getHistory();
    
    return history.filter(bill => {
      // Text search
      if (query) {
        const searchText = query.toLowerCase();
        const matchesId = bill.id.toLowerCase().includes(searchText);
        const matchesVenue = bill.venueName.toLowerCase().includes(searchText);
        const matchesParticipant = bill.participants.some(name => 
          name.toLowerCase().includes(searchText)
        );
        const matchesTotal = bill.total.toString().includes(searchText);
        const matchesVenueNotes = bill.experience?.venueNotes?.toLowerCase().includes(searchText);
        const matchesTags = bill.experience?.tags?.some(tag => 
          tag.toLowerCase().includes(searchText)
        );
        const matchesDishNotes = Object.values(bill.experience?.dishRatings || {}).some(dish =>
          dish.notes?.toLowerCase().includes(searchText)
        );
        
        if (!matchesId && !matchesVenue && !matchesParticipant && !matchesTotal && 
            !matchesVenueNotes && !matchesTags && !matchesDishNotes) {
          return false;
        }
      }
      
      // Archive filter
      if (filters.showArchived === false && bill.archived) {
        return false;
      }
      if (filters.showArchived === true && !bill.archived) {
        return false;
      }
      
      // Date range filter
      if (filters.dateFrom && bill.timestamp < new Date(filters.dateFrom).getTime()) {
        return false;
      }
      if (filters.dateTo && bill.timestamp > new Date(filters.dateTo).getTime()) {
        return false;
      }
      
      // Amount range filter
      if (filters.minAmount && bill.total < filters.minAmount) {
        return false;
      }
      if (filters.maxAmount && bill.total > filters.maxAmount) {
        return false;
      }
      
      // Rating filters
      if (filters.minRating && bill.experience?.venueRating && bill.experience.venueRating < filters.minRating) {
        return false;
      }
      if (filters.wouldReturn !== undefined && bill.experience?.wouldReturn !== filters.wouldReturn) {
        return false;
      }
      
      return true;
    });
  },

  // Get a specific bill for restoration
  getBill(billId) {
    const history = this.getHistory();
    return history.find(item => item.id === billId);
  },

  // Create a new bill from a historical one
  duplicateBill(billId, newBillId) {
    const originalBill = this.getBill(billId);
    if (!originalBill) return null;
    
    // Return the bill data structure that can be used to create a new bill
    return {
      id: newBillId,
      items: originalBill.billSnapshot.items.map(item => ({
        ...item,
        id: Date.now() + Math.random() // New IDs for items
      })),
      tax: originalBill.billSnapshot.tax,
      tip: originalBill.billSnapshot.tip,
      receiptImage: originalBill.billSnapshot.receiptImage,
      people: [], // Will be populated when people join
      assignments: {},
      hostId: null // Will be set by current user
    };
  },

  // Create a combined settlement from multiple historical bills
  createCombinedSettlement(billIds, historyData = null, simplifyDebts = true) {
    // If historyData provided (from cloud sync), use it; otherwise fall back to localStorage
    const historySource = historyData || this.getHistory();
    const billsToCombine = billIds.map(id => historySource.find(item => item.id === id)).filter(Boolean);
    if (billsToCombine.length < 2) return null;
    
    const combinedId = 'settlement_' + Date.now();
    const personTotals = {}; // person name -> { total, breakdown: [{ venue, amount }] }
    const allParticipants = new Set();
    let grandTotal = 0;
    
    // Process each bill's final balances
    billsToCombine.forEach(bill => {
      const venueName = bill.venueName || 'Unknown Venue';
      grandTotal += bill.total;
      
      // Add all participants
      bill.participants.forEach(name => allParticipants.add(name));
      
      // Get the final balances from the bill summary
      if (bill.summary && bill.summary.breakdown) {
        bill.summary.breakdown.forEach(person => {
          if (!personTotals[person.name]) {
            personTotals[person.name] = {
              total: 0,
              breakdown: [],
              paid: 0
            };
          }
          
          // Add this venue's amount to their total
          personTotals[person.name].total += person.finalShare;
          // For combined settlements, everyone starts fresh - no one has paid the combined total yet
          // personTotals[person.name].paid += person.paid;
          personTotals[person.name].breakdown.push({
            venue: venueName,
            amount: person.finalShare,
            paid: 0, // Reset - no one has paid the person who covered everything yet
            balance: -person.finalShare // Everyone owes their full share
          });
        });
      }
    });

    // For combined settlements, restore the original payment amounts from each bill
    // This shows who actually paid each bill, not who has the highest share
    billsToCombine.forEach((bill, index) => {
      if (bill.summary && bill.summary.breakdown) {
        bill.summary.breakdown.forEach(person => {
          if (personTotals[person.name]) {
            // Add back what they actually paid in this specific bill
            personTotals[person.name].paid += person.paid;
          }
        });
      }
    });

    // Generate transactions - simplified or basic depending on user choice
    const transactions = simplifyDebts 
      ? this._simplifyDebts(personTotals, billsToCombine.length)
      : this._basicTransactions(personTotals, billsToCombine.length);

    // Create the combined settlement data structure
    return {
      id: combinedId,
      venueName: `Combined: ${billsToCombine.map(b => b.venueName || 'Unknown').join(' + ')}`,
      timestamp: Date.now(),
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
      total: grandTotal,
      participants: Array.from(allParticipants),
      participantCount: allParticipants.size,
      status: 'combined_settlement',
      isCombinedSettlement: true,
      originalBills: billsToCombine.map(b => ({
        id: b.id,
        venueName: b.venueName || 'Unknown',
        total: b.total,
        date: b.date
      })),
      // Combined balances per person
      combinedBreakdown: Object.entries(personTotals)
        .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
        .map(([name, data]) => ({
          name,
          totalOwed: data.total,
          totalPaid: data.paid,
          finalBalance: data.paid - data.total, // Negative means they owe money
          venueBreakdown: data.breakdown
        })),
      // Settlement transactions
      summary: {
        transactions: transactions,
        breakdown: Object.entries(personTotals)
          .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
          .map(([name, data]) => ({
            name,
            finalShare: data.total,
            paid: data.paid,
            balance: data.paid - data.total
          }))
      }
    };
  },

  // Legacy function for raw item combining (keeping for backward compatibility)
  createCombinedBill(billIds) {
    const billsToCombine = billIds.map(id => this.getBill(id)).filter(Boolean);
    if (billsToCombine.length < 2) return null;
    
    const combinedId = 'combined_' + Date.now();
    const allItems = [];
    const allParticipants = new Set();
    let totalTax = 0;
    let totalTip = 0;
    
    billsToCombine.forEach((bill, index) => {
      // Add venue prefix to items to avoid conflicts
      const venuePrefix = bill.venueName || `Venue ${index + 1}`;
      bill.billSnapshot.items.forEach(item => {
        allItems.push({
          ...item,
          id: `${combinedId}_${item.id}_${index}`,
          name: `[${venuePrefix}] ${item.name}`
        });
      });
      
      // Collect all participants
      bill.participants.forEach(name => allParticipants.add(name));
      totalTax += bill.billSnapshot.tax || 0;
      totalTip += bill.billSnapshot.tip || 0;
    });
    
    // Return the combined bill data structure
    return {
      id: combinedId,
      items: allItems,
      tax: totalTax,
      tip: totalTip,
      receiptImage: null, // Combined bills don't have a single receipt
      people: Array.from(allParticipants).map((name, index) => ({ 
        name, 
        id: Date.now() + index 
      })),
      assignments: {},
      hostId: null,
      venueName: `Combined: ${billsToCombine.map(b => b.venueName || 'Unknown').join(' + ')}`,
      isCombined: true,
      originalBills: billsToCombine.map(b => ({ 
        id: b.id, 
        venueName: b.venueName || 'Unknown', 
        total: b.total,
        date: b.date
      }))
    };
  },

  // Get statistics
  getStats() {
    const history = this.getHistory();
    
    return {
      totalBills: history.length,
      archivedBills: history.filter(b => b.archived).length,
      totalSpent: history.reduce((sum, bill) => sum + bill.total, 0),
      averageBill: history.length > 0 ? history.reduce((sum, bill) => sum + bill.total, 0) / history.length : 0,
      mostFrequentParticipants: this._getMostFrequentParticipants(history),
      recentActivity: history.slice(0, 5)
    };
  },

  // Update experience data for a bill
  updateExperience(billId, experienceData) {
    try {
      let history = this.getHistory();
      const billIndex = history.findIndex(item => item.id === billId);
      
      if (billIndex !== -1) {
        // Merge new experience data with existing data
        history[billIndex].experience = {
          ...history[billIndex].experience,
          ...experienceData
        };
        localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
      }
      
      return history;
    } catch (error) {
      console.error("Could not update experience:", error);
      return this.getHistory();
    }
  },

  // Update dish rating for a specific item
  updateDishRating(billId, itemId, dishData) {
    try {
      let history = this.getHistory();
      const billIndex = history.findIndex(item => item.id === billId);
      
      if (billIndex !== -1) {
        if (!history[billIndex].experience.dishRatings) {
          history[billIndex].experience.dishRatings = {};
        }
        history[billIndex].experience.dishRatings[itemId] = dishData;
        localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
      }
      
      return history;
    } catch (error) {
      console.error("Could not update dish rating:", error);
      return this.getHistory();
    }
  },

  // Get venue statistics across multiple visits
  getVenueStats(venueName) {
    const history = this.getHistory();
    const venueVisits = history.filter(bill => 
      bill.venueName.toLowerCase() === venueName.toLowerCase()
    );

    if (venueVisits.length === 0) return null;

    const totalVisits = venueVisits.length;
    const ratings = venueVisits
      .map(visit => visit.experience.venueRating)
      .filter(rating => rating !== null);
    
    const avgRating = ratings.length > 0 
      ? ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length 
      : null;

    // Get most recommended dishes across all visits
    const allDishRatings = {};
    venueVisits.forEach(visit => {
      Object.entries(visit.experience.dishRatings || {}).forEach(([itemId, dishData]) => {
        const item = visit.billSnapshot.items.find(i => i.id.toString() === itemId);
        if (item && dishData.recommended) {
          const dishName = item.name.toLowerCase();
          if (!allDishRatings[dishName]) {
            allDishRatings[dishName] = { 
              name: item.name, 
              ratings: [], 
              recommendCount: 0,
              totalOrders: 0 
            };
          }
          allDishRatings[dishName].ratings.push(dishData.rating);
          allDishRatings[dishName].recommendCount++;
          allDishRatings[dishName].totalOrders++;
        }
      });
    });

    const recommendedDishes = Object.values(allDishRatings)
      .map(dish => ({
        ...dish,
        avgRating: dish.ratings.reduce((sum, r) => sum + r, 0) / dish.ratings.length,
        recommendPercent: (dish.recommendCount / dish.totalOrders) * 100
      }))
      .sort((a, b) => b.avgRating - a.avgRating)
      .slice(0, 5);

    return {
      venueName,
      totalVisits,
      avgRating,
      lastVisit: venueVisits[0].date, // Most recent first
      totalSpent: venueVisits.reduce((sum, visit) => sum + visit.total, 0),
      recommendedDishes,
      wouldReturnPercent: venueVisits.filter(v => v.experience.wouldReturn === true).length / totalVisits * 100
    };
  },

  _getMostFrequentParticipants(history) {
    const participantCounts = {};
    
    history.forEach(bill => {
      bill.participants.forEach(name => {
        participantCounts[name] = (participantCounts[name] || 0) + 1;
      });
    });
    
    return Object.entries(participantCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));
  },

  // === CLOUD SYNC METHODS ===
  
  // Get bill history with automatic cloud sync and migration
  async getHistoryWithSync(userId, db = null) {
    console.log('🔄 getHistoryWithSync called:', { userId, hasDb: !!db });
    if (!userId) {
      console.log('❌ No userId provided, falling back to localStorage only');
      return this.getHistory();
    }
    
    try {
      let cloudHistory = [];
      
      // Try to get history from Firestore first (if db provided)
      if (db) {
        try {
          console.log('☁️ Querying Firestore for bills:', `users/${userId}/billHistory`);
          const historyQuery = query(
            collection(db, `users/${userId}/billHistory`),
            orderBy('timestamp', 'desc'),
            limit(MAX_HISTORY_ITEMS)
          );
          const historySnapshot = await getDocs(historyQuery);
          
          console.log('📊 Firestore query result:', { 
            empty: historySnapshot.empty, 
            size: historySnapshot.size,
            docs: historySnapshot.docs.length 
          });
          
          if (!historySnapshot.empty) {
            cloudHistory = historySnapshot.docs.map(doc => ({
              ...doc.data(),
              id: doc.id
            }));
            console.log(`✅ Loaded ${cloudHistory.length} bills from Firestore for user:`, userId);
            console.log('📋 First 3 bills:', cloudHistory.slice(0, 3).map(b => ({ id: b.id, venueName: b.venueName, timestamp: b.timestamp })));
            return cloudHistory;
          }
        } catch (firestoreError) {
          console.log('Could not load from Firestore, falling back to localStorage:', firestoreError);
        }
      }

      // Fallback to localStorage
      const localHistory = this.getHistory();
      
      // If we have Firestore access and local data exists, migrate it
      if (db && localHistory && localHistory.length > 0) {
        try {
          console.log(`Migrating ${localHistory.length} bills to Firestore for user:`, userId);
          
          // Migrate bills to Firestore (batch them to avoid hitting limits)
          const migrationPromises = localHistory.slice(0, MAX_HISTORY_ITEMS).map(async (bill) => {
            const billDoc = doc(db, `users/${userId}/billHistory/${bill.id}`);
            await setDoc(billDoc, {
              ...bill,
              migratedAt: new Date().toISOString(),
              migratedFrom: 'localStorage'
            });
          });
          
          await Promise.all(migrationPromises);
          console.log('Bill history migrated from localStorage to Firestore for user:', userId);
          return localHistory;
        } catch (migrationError) {
          console.warn('Could not migrate bill history to Firestore:', migrationError);
        }
      }

      return localHistory;
    } catch (error) {
      console.error('Error in getHistoryWithSync:', error);
      return this.getHistory();
    }
  },

  // Add bill to history with cloud sync
  async addToHistoryWithSync(userId, billData, calculationResults, db = null) {
    console.log('💾 addToHistoryWithSync called:', { userId, hasDb: !!db, billId: billData?.id, venueName: billData?.venueName });
    if (!userId || !billData || !calculationResults) {
      console.log('❌ Missing required data, falling back to localStorage only');
      return this.addToHistory(billData, calculationResults);
    }

    try {
      const historyItem = {
        id: billData.id,
        venueName: billData.venueName || 'Unknown Venue',
        timestamp: Date.now(),
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        total: calculationResults.grandTotal,
        participants: billData.people.map(p => p.name),
        participantCount: billData.people.length,
        itemCount: billData.items.length,
        status: 'completed',
        archived: false,
        // Mark if this is a combined bill
        isCombined: billData.isCombined || false,
        originalBills: billData.originalBills || [],
        // Store minimal bill data for restoration
        billSnapshot: {
          items: billData.items,
          tax: billData.tax,
          tip: billData.tip,
          receiptImage: billData.receiptImage
        },
        // Dining experience data (preserve from billData)
        experience: billData.experience || {
          venueRating: null,
          venueNotes: '',
          overallRating: null,
          wouldReturn: null,
          dishRatings: {},
          tags: []
        },
        // Store results for quick display
        summary: {
          transactions: calculationResults.transactions,
          breakdown: calculationResults.breakdown.map(p => ({
            name: p.name,
            finalShare: p.finalShare,
            paid: p.paid,
            balance: p.balance,
            items: (p.items || []).map(i => ({ name: i.name, price: i.price })),
            itemTotal: p.itemTotal || 0,
            taxAndTipShare: p.taxAndTipShare || 0
          }))
        }
      };

      let firestoreSuccess = false;
      let localStorageSuccess = false;

      // Save to Firestore (primary storage)
      if (db) {
        try {
          console.log('☁️ Saving to Firestore:', `users/${userId}/billHistory/${historyItem.id}`);
          const billDoc = doc(db, `users/${userId}/billHistory/${historyItem.id}`);
          await setDoc(billDoc, historyItem);
          console.log('✅ Bill history saved to Firestore successfully:', { userId, billId: historyItem.id, venueName: historyItem.venueName });
          firestoreSuccess = true;
        } catch (firestoreError) {
          console.error('❌ Could not save bill history to Firestore:', firestoreError);
        }
      } else {
        console.log('⚠️ No database connection, skipping Firestore save');
      }

      // Always also save to localStorage (for offline access)
      try {
        const localResult = this.addToHistory(billData, calculationResults);
        console.log('Bill history saved to localStorage for user:', userId);
        localStorageSuccess = true;
        
        // Return the local result if Firestore failed
        if (!firestoreSuccess) return localResult;
      } catch (localError) {
        console.error('Could not save bill history to localStorage:', localError);
      }

      // If both failed, return the current history
      if (!firestoreSuccess && !localStorageSuccess) {
        return this.getHistory();
      }

      // Return updated history
      return await this.getHistoryWithSync(userId, db);
    } catch (error) {
      console.error('Error in addToHistoryWithSync:', error);
      return this.addToHistory(billData, calculationResults);
    }
  },

  // Delete bill from history with cloud sync
  async deleteBillWithSync(userId, billId, db = null) {
    console.log('🗑️ deleteBillWithSync called:', { userId, billId, hasDb: !!db });
    if (!billId) {
      console.log('❌ No billId provided');
      return this.getHistory();
    }

    try {
      let firestoreSuccess = false;
      let localStorageSuccess = false;

      // Delete from Firestore (primary storage)
      if (userId && db) {
        try {
          console.log('☁️ Deleting from Firestore:', `users/${userId}/billHistory/${billId}`);
          const billDoc = doc(db, `users/${userId}/billHistory/${billId}`);
          await deleteDoc(billDoc);
          console.log('✅ Bill deleted from Firestore successfully:', { userId, billId });
          firestoreSuccess = true;
        } catch (firestoreError) {
          console.error('❌ Could not delete bill from Firestore:', firestoreError);
        }
      } else {
        console.log('⚠️ No user/db connection, skipping Firestore delete');
      }

      // Always also delete from localStorage (for offline access)
      try {
        const localResult = this.deleteBill(billId);
        console.log('✅ Bill deleted from localStorage:', billId);
        localStorageSuccess = true;
        
        // Return the local result if Firestore failed
        if (!firestoreSuccess && !userId) return localResult;
      } catch (localError) {
        console.error('❌ Could not delete bill from localStorage:', localError);
      }

      // If both failed, return the current history
      if (!firestoreSuccess && !localStorageSuccess) {
        return this.getHistory();
      }

      // Return updated history
      if (userId && db) {
        return await this.getHistoryWithSync(userId, db);
      } else {
        return this.getHistory();
      }
    } catch (error) {
      console.error('Error in deleteBillWithSync:', error);
      return this.deleteBill(billId);
    }
  },

  // Advanced debt simplification algorithm (inspired by Splitwise)
  _simplifyDebts(personTotals, billCount) {
    const transactions = [];
    // Sort entries by name for consistent processing order
    const balances = Object.entries(personTotals)
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .map(([name, data]) => ({
        name,
        balance: data.paid - data.total // Positive means they're owed money, negative means they owe
      }));

    // Remove people with zero balance (within rounding tolerance)
    const nonZeroBalances = balances.filter(p => Math.abs(p.balance) > 0.01);
    
    if (nonZeroBalances.length === 0) return [];

    // Step 1: Skip cycle elimination for combined settlements - use simple greedy approach
    const optimizedBalances = [...nonZeroBalances];
    
    // Step 2: Use greedy algorithm for debts - sort deterministically
    const creditors = optimizedBalances
      .filter(p => p.balance > 0)
      .sort((a, b) => {
        // Sort by balance descending, then by name for consistency
        const balanceDiff = b.balance - a.balance;
        return balanceDiff !== 0 ? balanceDiff : a.name.localeCompare(b.name);
      });
    
    const debtors = optimizedBalances
      .filter(p => p.balance < 0)
      .sort((a, b) => {
        // Sort by balance ascending (most negative first), then by name for consistency  
        const balanceDiff = a.balance - b.balance;
        return balanceDiff !== 0 ? balanceDiff : a.name.localeCompare(b.name);
      });

    // Step 3: Create minimal transactions
    let creditorIndex = 0;
    let debtorIndex = 0;

    while (creditorIndex < creditors.length && debtorIndex < debtors.length) {
      const creditor = creditors[creditorIndex];
      const debtor = debtors[debtorIndex];
      
      const amountToSettle = Math.min(creditor.balance, Math.abs(debtor.balance));
      
      if (amountToSettle > 0.01) {
        transactions.push({
          id: `transaction_${transactions.length + 1}`,
          from: debtor.name,
          to: creditor.name,
          amount: amountToSettle,
          description: billCount > 1 ? `Settlement across ${billCount} bills` : 'Settlement',
          simplified: true
        });
        
        creditor.balance -= amountToSettle;
        debtor.balance += amountToSettle;
      }
      
      // Move to next creditor/debtor if current one is settled
      if (creditor.balance < 0.01) creditorIndex++;
      if (Math.abs(debtor.balance) < 0.01) debtorIndex++;
    }

    return transactions;
  },

  // Eliminate debt cycles to reduce transaction count
  _eliminateCycles(balances) {
    const optimized = [...balances];
    
    // Find and eliminate 3-person cycles (A owes B, B owes C, C owes A)
    for (let i = 0; i < optimized.length; i++) {
      for (let j = 0; j < optimized.length; j++) {
        for (let k = 0; k < optimized.length; k++) {
          if (i === j || j === k || i === k) continue;
          
          const person1 = optimized[i]; // Should be debtor
          const person2 = optimized[j]; // Should be creditor to person1, debtor to person3
          const person3 = optimized[k]; // Should be creditor
          
          // Check if we have a cycle: person1 owes money, person2 is owed money but also owes money, person3 is owed money
          if (person1.balance < -0.01 && person2.balance < -0.01 && person3.balance > 0.01) {
            // Find the minimum amount that can be cycled
            const cycleAmount = Math.min(
              Math.abs(person1.balance),
              Math.abs(person2.balance),
              person3.balance
            );
            
            if (cycleAmount > 0.01) {
              // Eliminate the cycle
              person1.balance += cycleAmount;
              person2.balance += cycleAmount;
              person3.balance -= cycleAmount;
            }
          }
        }
      }
    }

    return optimized;
  },

  // Basic transaction generation without simplification
  _basicTransactions(personTotals, billCount) {
    const transactions = [];
    // Sort entries by name for consistent processing order
    const balances = Object.entries(personTotals)
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .map(([name, data]) => ({
        name,
        balance: data.paid - data.total // Positive means they're owed money, negative means they owe
      }));

    // Remove people with zero balance (within rounding tolerance)
    const nonZeroBalances = balances.filter(p => Math.abs(p.balance) > 0.01);
    
    if (nonZeroBalances.length === 0) return [];

    // Simple greedy algorithm without cycle elimination - sort deterministically
    const creditors = nonZeroBalances
      .filter(p => p.balance > 0)
      .sort((a, b) => {
        // Sort by balance descending, then by name for consistency
        const balanceDiff = b.balance - a.balance;
        return balanceDiff !== 0 ? balanceDiff : a.name.localeCompare(b.name);
      });
    
    const debtors = nonZeroBalances
      .filter(p => p.balance < 0)
      .sort((a, b) => {
        // Sort by balance ascending (most negative first), then by name for consistency
        const balanceDiff = a.balance - b.balance;
        return balanceDiff !== 0 ? balanceDiff : a.name.localeCompare(b.name);
      });

    let creditorIndex = 0;
    let debtorIndex = 0;

    while (creditorIndex < creditors.length && debtorIndex < debtors.length) {
      const creditor = creditors[creditorIndex];
      const debtor = debtors[debtorIndex];
      
      const amountToSettle = Math.min(creditor.balance, Math.abs(debtor.balance));
      
      if (amountToSettle > 0.01) {
        transactions.push({
          id: `transaction_${transactions.length + 1}`,
          from: debtor.name,
          to: creditor.name,
          amount: amountToSettle,
          description: billCount > 1 ? `Settlement across ${billCount} bills` : 'Settlement',
          simplified: false
        });
        
        creditor.balance -= amountToSettle;
        debtor.balance += amountToSettle;
      }
      
      // Move to next creditor/debtor if current one is settled
      if (creditor.balance < 0.01) creditorIndex++;
      if (Math.abs(debtor.balance) < 0.01) debtorIndex++;
    }

    return transactions;
  }
};